import React, { useState } from "react"
import { Box, Text } from "@chakra-ui/react"
import ReactDatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import { parseISO, startOfDay } from "date-fns"
import { Combobox } from "components/Combobox"

const applyFooterStyle = (isDirty: boolean) => ({
  // role="button" because Apply commits, not a listbox option.
  role: "button" as const,
  "aria-disabled": !isDirty,
  py: 2,
  px: 0,
  bg: "transparent",
  borderTopWidth: 1,
  borderColor: "gray.200",
  justifyContent: "center" as const,
  cursor: isDirty ? "pointer" : "not-allowed",
  transition: "background-color 0.2s ease",
  // sx (not _hover/_active) so :active follows :hover in source order
  // and wins the same-specificity tie on click+hold.
  ...(isDirty && {
    sx: {
      "&:hover": { bg: "blue.50" },
      "&:active": { bg: "blue.100" },
    },
  }),
})

// Wire format = midnight UTC of the picked calendar day. The backend's
// segment_viewer compares against `date_trunc('day', ... at time zone
// events.timezone)`, which is a naive midnight. `toISOString()` on a
// local-midnight Date shifts by the viewer's offset and silently misses.
export const dayToUtcIso = (d: Date): string => (
  new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString()
)

// Inverse: read the ISO's UTC parts and build a local-midnight Date so
// react-datepicker shows the right calendar day regardless of viewer TZ.
export const utcIsoToLocalDay = (iso: string): Date | null => {
  try {
    const d = parseISO(iso)
    if (Number.isNaN(d.getTime())) return null

    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
  } catch {
    return null
  }
}

type Props = {
  initialValue?: string
  onCommit: (values: string[]) => void
}

const DatePicker = ({ initialValue, onCommit }: Props) => {
  const initial = initialValue ? utcIsoToLocalDay(initialValue) : null
  const [draft, setDraft] = useState<Date | null>(initial ?? startOfDay(new Date()))
  const isEdit = initialValue !== undefined
  const isDirty = (() => {
    if (!isEdit) return true
    if (!draft && !initial) return false
    if (!draft || !initial) return true

    return draft.getTime() !== initial.getTime()
  })()

  const apply = () => {
    if (!isDirty || !draft) return
    onCommit([dayToUtcIso(draft)])
  }

  return (
    <Box>
      <Box p={3} display="flex" justifyContent="center">
        <ReactDatePicker selected={draft} onChange={(d) => setDraft(d)} inline />
      </Box>
      <Combobox.Action
        onSelect={apply}
        closeOnSelect={isDirty && !!draft}
        {...applyFooterStyle(isDirty)}
      >
        <Text color="blue.500" fontWeight="semibold" opacity={isDirty ? 1 : 0.4}>
          Apply
        </Text>
      </Combobox.Action>
    </Box>
  )
}

export default DatePicker
